import { APIAction, APIActionGenerator } from '../apidata';
import { APIResult } from '../api';
import {
  PaginatedAPIRequest,
  PaginationSagaOptions,
  PaginationOptions,
  PaginationSelector,
  PaginatedAPIData,
} from './common';
import { takeEvery, call, put, select } from 'redux-saga/effects';
import { ForkEffect } from '@redux-saga/core/effects';

export function paginationCustomSaga<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  generator: APIActionGenerator<R, T, E>,
  sagaOpts: PaginationSagaOptions<R, T, E>
): ForkEffect {
  const { api, types, handleResponse } = generator;
  return takeEvery(
    // Configure the given request to be handled by this saga
    types.request,
    function* (action: APIAction<R, T>) {
      const { request } = action;
      if (request === undefined) {
        return;
      }

      const { generatePage } = sagaOpts;
      const page: string | undefined = yield call(generatePage, request);
      if (!page) {
        // There are no pages left, so there's nothing to do here.
        return;
      }

      const response: APIResult<T, E> = yield call(api.get, page);
      yield call(handleResponse, { request, response });

      const { success } = response;
      if (!success) {
        return;
      }

      const { onSuccess } = sagaOpts;
      if (onSuccess) {
        yield call(onSuccess, request, response);
      }

      const { fetchAll } = request;
      if (fetchAll) {
        const { translatePage } = opts;
        const { nextPage } = translatePage(response.data);
        if (nextPage) {
          request.page = nextPage;

          // We wouldn't want to reset on subsequent requests - only the first
          // one.
          request.reset = false;
          yield put(generator.request(request));
        }
      }
    }
  );
}

export function paginationSaga<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  opts: PaginationOptions<R, T, P, E>,
  generator: APIActionGenerator<R, T, E>,
  getInitialURL: (request: R) => string,
  selector: PaginationSelector<R, P, E>
): ForkEffect {
  return paginationCustomSaga(opts, generator, {
    generatePage: defaultPageGenerator<R, T, P, E>(getInitialURL, selector),
  });
}

export function defaultPageGenerator<
  R extends PaginatedAPIRequest<T, E>,
  T = unknown,
  P = unknown,
  E = unknown
>(
  getInitialURL: (request: R) => string,
  selector: PaginationSelector<R, P, E>
) {
  return function* (request: R): Generator<any, string | undefined, any> {
    if (request.page) {
      return request.page;
    }

    const initialUrl = yield call(getInitialURL, request);
    if (request.reset) {
      return initialUrl;
    }

    const pages: PaginatedAPIData<unknown, E> = yield select(selector, request);
    if (!pages.isFilled) {
      return initialUrl;
    }

    return pages.nextPage;
  };
}
